import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Loader2, Activity, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '../components/Footer';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Bienvenido al Espacio de Datos AEA');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  };

  return (
    <>
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Panel izquierdo - aluminum brush */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-aea-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: 'radial-gradient(circle at 25% 30%, rgba(3,105,161,0.4) 0%, transparent 40%), radial-gradient(circle at 75% 70%, rgba(148,163,184,0.25) 0%, transparent 40%)'
        }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg aluminum-bg flex items-center justify-center shadow-xl">
              <Activity className="w-7 h-7 text-aea-navy" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-outfit font-bold text-2xl leading-none">AEA</div>
              <div className="text-slate-400 text-xs mt-1">Espacio de Datos Sectorial</div>
            </div>
          </div>
        </div>

        <div className="relative animate-fade-in-up">
          <h1 className="font-outfit font-bold text-4xl leading-tight mb-4">
            Inteligencia sectorial<br/>
            <span className="text-aea-silver">del aluminio</span> en tiempo real
          </h1>
          <p className="text-slate-300 max-w-md leading-relaxed">
            Aporta tus datos mensuales de producción, energía, exportaciones y reciclaje.
            Recibe estadísticas agregadas anónimas del sector y compara tu evolución con la media española.
          </p>
          <div className="grid grid-cols-3 gap-4 mt-8 max-w-md">
            {[
              { k: 'Propiedad', v: 'Tus datos, tuyos.' },
              { k: 'Anónimo', v: 'k-anon ≥3' },
              { k: 'Agente IA', v: 'Insights al instante' },
            ].map((f) => (
              <div key={f.k} className="border-l-2 border-aea-blue pl-3">
                <div className="text-white text-sm font-semibold">{f.k}</div>
                <div className="text-slate-400 text-xs mt-1">{f.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-xs text-slate-500">
          Asociación Española del Aluminio y Tratamientos de Superficie · Espacio de Datos Sectorial
        </div>
      </div>

      {/* Panel derecho - login form */}
      <div className="flex flex-col justify-center px-8 lg:px-16 py-12 bg-slate-50">
        <div className="max-w-md w-full mx-auto">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg aluminum-bg flex items-center justify-center">
              <Activity className="w-6 h-6 text-aea-navy" strokeWidth={2.5} />
            </div>
            <span className="font-outfit font-bold text-xl">AEA · Espacio de Datos</span>
          </div>

          <h2 className="font-outfit font-bold text-3xl text-slate-900">Acceder al espacio</h2>
          <p className="text-slate-500 mt-2">Introduce tus credenciales para continuar.</p>

          <Card className="mt-8 border-slate-200 shadow-sm">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Correo electrónico</Label>
                  <Input id="email" type="email" placeholder="tucorreo@empresa.es"
                    data-testid="login-email-input"
                    value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Contraseña</Label>
                  <Input id="password" type="password" placeholder="••••••••"
                    data-testid="login-password-input"
                    value={password} onChange={(e) => setPassword(e.target.value)} required />
                </div>
                <Button type="submit" className="w-full bg-aea-blue hover:bg-aea-blue-dk" disabled={loading} data-testid="login-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Entrar <ArrowRight className="ml-2 w-4 h-4" /></>)}
                </Button>
              </form>

              
            </CardContent>
          </Card>

          <p className="text-center text-sm text-slate-500 mt-6">
            ¿Aún no eres asociado? <Link to="/register" className="text-aea-blue font-medium hover:underline" data-testid="link-register">Solicitar alta</Link>
          </p>
          <p className="text-center text-xs text-slate-400 mt-3">
            <Link to="/">← Volver al inicio</Link>
          </p>
        </div>
      </div>
    </div>
    <Footer />
    </>
  );
}
